import { Permit, RecoveryController } from './controller';
import { RecoveryError } from './errors';
import { copyRequest } from './copyRequest';

export interface DispatchGuard {
  readonly assertActive: () => void;
  readonly subscribe: (listener: () => void) => () => void;
  readonly deadline: number;
}

/** Next.js answers every Server Action with RSC, so a non-RSC 403 was produced in front of it. */
export function isEdgeRejection(response: Response): boolean {
  return (
    response.status === 403 &&
    !(response.headers.get('Content-Type') ?? '').startsWith('text/x-component')
  );
}

function discardBody(body: ReadableStream | null): void {
  if (body && !body.locked) {
    void body.cancel().catch((): void => {});
  }
}

/** Resolves the URL of a same-origin Server Action POST, or null for any other request. */
export function serverActionUrl(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  origin: string,
  documentUrl: string
): URL | null {
  const url = new URL(input instanceof Request ? input.url : String(input), documentUrl);
  const method = init?.method ?? (input instanceof Request ? input.method : 'GET');
  const headers = new Headers(
    init?.headers ?? (input instanceof Request ? input.headers : undefined)
  );

  return url.origin === origin && method.toUpperCase() === 'POST' && headers.has('Next-Action')
    ? url
    : null;
}

export function requestSignal(
  input: RequestInfo | URL,
  init: RequestInit | undefined
): AbortSignal | undefined {
  return init && 'signal' in init
    ? (init.signal ?? undefined)
    : input instanceof Request
      ? input.signal
      : undefined;
}

export function createRecoveryFetch(
  original: typeof fetch,
  controller: RecoveryController,
  origin: string,
  documentUrl: () => string = (): string => origin,
  prepareDispatch?: (signal: AbortSignal | undefined, guard: DispatchGuard) => Promise<void>
): typeof fetch {
  return async (input, init): Promise<Response> => {
    const url = serverActionUrl(input, init, origin, documentUrl());

    let result: Response | Promise<Response>;
    if (url === null) {
      result = original(input, init);
    } else {
      const generation = controller.ownership;
      const previousCompletion = controller.completion;
      const signal = requestSignal(input, init);
      // Join synchronously: capture may outlive completion, cancellation or the deadline.
      let held = controller.waiting;
      let dispatchPermit: Permit | undefined;
      const heldPermit = held
        ? controller.wait(signal).then(
            (permit) => ({ permit, error: null }),
            (error: unknown) => ({ permit: null, error })
          )
        : null;
      const assertPermit = (permit: Permit): void => {
        try {
          controller.assertActive(generation, signal, permit.deadline);
        } catch (error) {
          if (
            error instanceof RecoveryError &&
            error.outcome === 'timed-out' &&
            controller.ownership === generation &&
            !controller.waiting &&
            controller.getSnapshot().outcome === null &&
            controller.completion?.generation === permit.generation &&
            controller.completion?.deadline === permit.deadline
          ) {
            controller.fail('timed-out');
          }
          throw error;
        }
      };
      const waitIfHeld = async (deadline = Infinity): Promise<void> => {
        controller.assertActive(generation, signal);
        if (heldPermit) {
          const result = await heldPermit;
          if (result.error) {
            throw result.error;
          }
          if (result.permit) {
            dispatchPermit = result.permit;
            assertPermit(result.permit);
          }
        }
        if (controller.waiting) {
          held = true;
          dispatchPermit = await controller.wait(signal, deadline);
          assertPermit(dispatchPermit);
        } else if (controller.completion && controller.completion !== previousCompletion) {
          held = true;
          dispatchPermit = controller.completion;
          assertPermit(dispatchPermit);
        }
      };
      let preparationPermit: Permit | undefined;
      const assertDispatchActive = (): void => {
        if (preparationPermit) {
          assertPermit(preparationPermit);
        }
        if (dispatchPermit && dispatchPermit !== preparationPermit) {
          assertPermit(dispatchPermit);
        } else if (!preparationPermit) {
          controller.assertActive(generation, signal);
        }
      };
      const dispatch = (
        target: RequestInfo | URL,
        targetInit: RequestInit | undefined,
        onDispatch: () => void
      ): Promise<Response> => {
        assertDispatchActive();
        const response = original(target, targetInit);
        onDispatch();
        return response;
      };
      const preparedDispatch = async (
        target: RequestInfo | URL,
        targetInit: RequestInit | undefined,
        onDispatch: () => void
      ): Promise<Response> => {
        preparationPermit = dispatchPermit;
        let response: Promise<Response> | undefined;
        while (!response) {
          await waitIfHeld(preparationPermit?.deadline ?? Infinity);
          if (
            dispatchPermit &&
            (!preparationPermit || dispatchPermit.deadline < preparationPermit.deadline)
          ) {
            preparationPermit = dispatchPermit;
          }
          if (prepareDispatch) {
            await prepareDispatch(signal, {
              assertActive: assertDispatchActive,
              subscribe: controller.subscribe,
              deadline: Math.min(
                preparationPermit?.deadline ?? Infinity,
                dispatchPermit?.deadline ?? Infinity
              )
            });
          }
          // A new episode requires another recovery wait followed by fresh challenge preparation.
          if (!controller.waiting) {
            if (controller.completion && controller.completion !== previousCompletion) {
              held = true;
              dispatchPermit = controller.completion;
            }
            response = dispatch(target, targetInit, onDispatch);
          }
        }
        return response;
      };
      const send = prepareDispatch ? preparedDispatch : dispatch;
      const sendOnce = async (): Promise<Response> => {
        await waitIfHeld();
        const response = await send(input, init, (): void => {});
        if (held && isEdgeRejection(response)) {
          discardBody(response.body);
          controller.assertActive(generation, signal);
          controller.fail('retry-rejected');
          throw new RecoveryError('retry-rejected');
        }
        return response;
      };
      let request: Request | undefined;
      let replay: Request | undefined;
      // Streaming bodies are not bounded replay candidates. Never buffer them eagerly.
      if (
        !(init?.body instanceof ReadableStream) &&
        !(input instanceof Request && input.bodyUsed)
      ) {
        try {
          const source = input instanceof Request ? input : new Request(url, init);
          try {
            request =
              (await copyRequest(source, input instanceof Request ? init : undefined)) ?? undefined;
          } finally {
            if (!(input instanceof Request)) {
              discardBody(source.body);
            }
          }
          if (request) {
            replay = request.clone();
          }
        } catch {
          request = undefined;
        }
      }
      if (request && replay) {
        let firstDispatched = false;
        let replayDispatched = false;
        try {
          controller.assertActive(generation, signal ?? undefined);
          await waitIfHeld();
          let response = await send(request, undefined, (): void => {
            firstDispatched = true;
          });
          if (isEdgeRejection(response)) {
            discardBody(response.body);
            controller.assertActive(generation, signal ?? undefined);
            if (held) {
              controller.fail('retry-rejected');
              throw new RecoveryError('retry-rejected');
            }
            const permit =
              controller.completion !== previousCompletion && controller.completion
                ? controller.completion
                : await controller.wait(signal ?? undefined);
            assertPermit(permit);
            dispatchPermit = permit;
            response = await send(replay, undefined, (): void => {
              controller.recordRetry();
              replayDispatched = true;
            });
            if (isEdgeRejection(response)) {
              discardBody(response.body);
              controller.assertActive(generation, signal ?? undefined);
              controller.fail('retry-rejected');
              throw new RecoveryError('retry-rejected');
            }
          }
          result = response;
        } finally {
          if (!firstDispatched) {
            discardBody(request.body);
          }
          if (!replayDispatched) {
            discardBody(replay.body);
          }
          replay = undefined;
        }
      } else {
        result = sendOnce();
      }
    }
    return result;
  };
}
